import { Decimal } from 'decimal.js';
import { Schemas } from '@api-client/generated/types';
import { DEFAULT_CURRENCY_CODE } from '@constants';
import { BookkeepingDocumentMetadataDto, BookingDocumentMetadata } from './types';

// ------------------------------------------------------------------------------------------------
// ITEMS CALCULATIONS
// ------------------------------------------------------------------------------------------------

const validateAndGetBaseValuesForVats = (
    item: Schemas.BookingItemDto,
    vats: Schemas.BobVAT[] = []
) => {
    // NOTE: Simple check for 'submittable' variable to ensure that all the necessary variables for calculations are filled in in the form doesn't work here.
    // E.g. if you remove amount from the form, the check will still pass and an error will appear immediately. So, we need to check everything here.


    if (item?.netPrice === undefined || item?.netPrice === null || isNaN(item?.netPrice)) {
        return null;
    }

    if (item?.vatCode === undefined || item?.vatCode === null || item?.vatCode === '') {
        return null;
    }

    if (vats.length === 0) {
        return null;
    }

    const vatFound = vats.find((vat) => vat.id === item.vatCode);
    if (!vatFound) {
        return null;
    }

    const netPrice = new Decimal(item.netPrice)
    const vatRate = new Decimal(vatFound.rate)

    return {
        netPrice,
        vatRate,
    };
};

const getUneffectiveTaxAmountForItem = (
    item: Schemas.BookingItemDto,
    vats: Schemas.BobVAT[] = []
) => {
    const baseValues = validateAndGetBaseValuesForVats(item, vats);
    if (!baseValues) return null;

    const { netPrice, vatRate } = baseValues;
    return netPrice.times(vatRate).dividedBy(100).toDecimalPlaces(2).toNumber();
};

const getTotalPriceForItem = (
    item: Schemas.BookingItemDto,
) => {
    if (item.netPrice === undefined || item.netPrice === null || isNaN(item.netPrice)) {
        return null;
    }

    if (item.taxAmount === undefined || item.taxAmount === null || isNaN(item.taxAmount)) {
        return null;
    }

    return item.isEffectiveVatZero ? new Decimal(item.netPrice).toDecimalPlaces(2).toNumber() : new Decimal(item.netPrice).plus(item.taxAmount).toDecimalPlaces(2).toNumber();
};


export const updateItemTaxAmountByIndex = (
    index: number,
    items: Schemas.BookingItemDto[],
    vats: Schemas.BobVAT[] = []
): Schemas.BookingItemDto[] => {
    const taxAmount = getUneffectiveTaxAmountForItem(items[index], vats);
    const updatedItems = [...items];
    updatedItems[index] = {
        ...updatedItems[index],
        taxAmount: taxAmount
    };
    return updatedItems;
};

export const updateItemTotalPriceByIndex = (
    index: number,
    items: Schemas.BookingItemDto[]
): Schemas.BookingItemDto[] => {
    const totalPrice = getTotalPriceForItem(items[index]);
    const updatedItems = [...items];
    updatedItems[index] = {
        ...updatedItems[index],
        totalPrice: totalPrice
    };
    return updatedItems;
};

export const updateItemEurNetPriceByIndex = (
    index: number,
    items: Schemas.BookingItemDto[],
    currency: string | null | undefined,
    exchangeRate: number | null | undefined
): Schemas.BookingItemDto[] => {
    const netPrice = items[index].netPrice;
    const eurNetPrice = convertToCurrencyByExchangeRate(netPrice, currency, exchangeRate);
    const updatedItems = [...items];
    updatedItems[index] = {
        ...updatedItems[index],
        eurNetPrice: eurNetPrice
    };
    return updatedItems;
};

export const updateItemEurTaxAmountByIndex = (
    index: number,
    items: Schemas.BookingItemDto[],
    currency: string | null | undefined,
    exchangeRate: number | null | undefined
): Schemas.BookingItemDto[] => {
    const taxAmount = items[index].taxAmount;
    const eurTaxAmount = convertToCurrencyByExchangeRate(taxAmount, currency, exchangeRate);
    const updatedItems = [...items];
    updatedItems[index] = {
        ...updatedItems[index],
        eurTaxAmount: eurTaxAmount
    };
    return updatedItems;
};

export const updateEurTotalPriceByIndex = (
    index: number,
    items: Schemas.BookingItemDto[]
): Schemas.BookingItemDto[] => {
    const eurNetPrice = items[index].eurNetPrice;
    let eurTaxAmount = items[index].eurTaxAmount;

    const netAndTotalPricesExist = eurNetPrice !== undefined && eurTaxAmount !== undefined && eurNetPrice !== null && eurTaxAmount !== null;

    eurTaxAmount = items[index].isEffectiveVatZero ? 0 : eurTaxAmount;

    const eurTotalPrice = netAndTotalPricesExist ? new Decimal(eurNetPrice).plus(eurTaxAmount!).toDecimalPlaces(2).toNumber() : null;

    const updatedItems = [...items];
    updatedItems[index] = {
        ...updatedItems[index],
        eurTotalPrice: eurTotalPrice
    };

    return updatedItems;
};

// ------------------------------------------------------------------------------------------------
// DOCUMENT CALCULATIONS
// ------------------------------------------------------------------------------------------------

export const calculateDocumentEurAmount = (
    amount: number | null | undefined,
    currency: string | null | undefined,
    exchangeRate: number | null | undefined
): number | null => convertToCurrencyByExchangeRate(amount, currency, exchangeRate);

// NOTE: `items` lives in a free-form `jsonb` column and can arrive as a value
// that is NOT the `BookingItem[]` the type promises. Observed on stage
// (2026-07, 41 documents): `items` is stored as a **double-encoded JSON string**
// — the literal `'[{"name":"…","totalPrice":10.67}]'` instead of an array — so
// `items.some(...)` threw `items.some is not a function` and white-screened the
// whole expense form (Ant Design's `<Form.List>` would crash on it too).
//
// We RECOVER the real items rather than drop them, so opening the form never
// loses the line items and the next save rewrites a proper array (self-heal).
// Returning `[]` would let a later save persist an empty list over the real
// data, which we must not do — so `[]` is strictly the last resort, used only
// when nothing is recoverable:
//   - array        → passed through unchanged
//   - JSON string  → parsed and re-normalised (recovers the stringified array)
//   - plain object → unwrapped with `Object.values` (array-turned-object)
//   - otherwise    → `[]`
export const normalizeBookingItems = (
    items: unknown
): Schemas.BookingItemDto[] => {
    if (Array.isArray(items)) return items;
    if (typeof items === 'string') {
        try {
            return normalizeBookingItems(JSON.parse(items));
        } catch {
            return [];
        }
    }
    if (items && typeof items === 'object') {
        return Object.values(items) as Schemas.BookingItemDto[];
    }
    return [];
};

const validateTotalAmountForDocument = (
    values: BookkeepingDocumentMetadataDto | BookingDocumentMetadata = {},
    isEuro: boolean = false
) => {
    const totalField = isEuro ? 'eurAmount' : 'amount';
    const itemField = isEuro ? 'eurTotalPrice' : 'totalPrice';
    const items = normalizeBookingItems(values.items);

    if (items.length === 0) return { isValid: false, total: null, itemsTotal: null };

    const totalExists = values?.[totalField] !== undefined && values?.[totalField] !== null;
    if (!totalExists) return { isValid: false, total: null, itemsTotal: null };

    const hasMissingItemValues = items.some(item =>
        !item || // NOTE: Necessary when we add the first item, it's [undefined] initially
        item[itemField] === undefined ||
        item[itemField] === null
    );

    if (hasMissingItemValues) return { isValid: false, total: values[totalField], itemsTotal: null };

    const itemsTotal = items.reduce(
        (total, item) => total.plus(new Decimal(item[itemField]!)),
        new Decimal(0)
    );

    const total = new Decimal(values[totalField]!);
    const isValid = total.equals(itemsTotal);

    return {
        isValid,
        total: total.toNumber(),
        itemsTotal: itemsTotal.toNumber()
    };
};

export const getStatusByAmount = (
    values: BookkeepingDocumentMetadataDto | BookingDocumentMetadata = {}
) => validateTotalAmountForDocument(values, false);

export const getStatusByEurAmount = (
    values: BookkeepingDocumentMetadataDto | BookingDocumentMetadata = {}
) => validateTotalAmountForDocument(values, true);

// ------------------------------------------------------------------------------------------------
// BOTH
// ------------------------------------------------------------------------------------------------

export const resetEurValues = (values: BookkeepingDocumentMetadataDto, items: Schemas.BookingItemDto[]) => {
    const updatedValues = { ...values };
    updatedValues.fromEurRate = null;
    updatedValues.eurAmount = null;

    if (items && items.length > 0) {
        updatedValues.items = items.map((item: Schemas.BookingItemDto) => ({
            ...item,
            eurNetPrice: null,
            eurTaxAmount: null,
            eurTotalPrice: null
        }));
    }

    return updatedValues;
};

const convertToCurrencyByExchangeRate = (
    amount: number | null | undefined,
    currency: string | null | undefined,
    exchangeRate: number | null | undefined
): number | null => {
    if (amount === undefined || amount === null) return null;

    if (currency === null || currency === undefined || currency === DEFAULT_CURRENCY_CODE) return null;

    if (exchangeRate === undefined || exchangeRate === null || exchangeRate === 0) return null;

    return new Decimal(amount).dividedBy(exchangeRate).toDecimalPlaces(2).toNumber();
};